import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { RubyPlatformProvider, RubyWidget } from "@attmos/ruby-ui";

import { useSession } from "@/api/auth";
import { Footer } from "@/components/layout/Footer";
import { Sidebar } from "@/components/layout/Sidebar";
import { RunCommandPalette } from "@/components/RunCommandPalette";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { createFlowStackAdapter } from "@/ruby/adapter";

/**
 * App shell — a full-height left rail with the workspace column
 * (content outlet + © line) beside it.
 *
 * **The outer axis is a ROW** (flowstack#953). It used to be a column
 * — Header, then a row of [Sidebar, main], then Footer — which boxed
 * the rail between two edge-to-edge bars. Now the rail is the first
 * thing on the page and spans the viewport top to bottom.
 *
 * **And there is no top bar at all** (flowstack#1002). The rail is the
 * app's only chrome: its three occupants — the lockup, the collapse
 * toggle and the ⌘K trigger — moved into the rail's own top and bottom
 * zones, so ``<main>`` now starts at y = 0 and every page gets the
 * 56px back. Page-specific verbs belong to the page's own top-right
 * row, not to global chrome; nothing here blocks one.
 *
 * Wires the global cmd-K palette + sonner toaster + tooltip provider
 * so every route gets them for free. Route transitions are a 120ms
 * opacity-only crossfade (no slide, no scale — anything more makes
 * the app feel sluggish), with prefers-reduced-motion degrading to
 * instant.
 */
export function AppShell(): JSX.Element {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const { data: session } = useSession();
  const adapter = useMemo(() => createFlowStackAdapter(session ?? null), [session]);

  return (
    <RubyPlatformProvider adapter={adapter}>
      <TooltipProvider delayDuration={200}>
        <div className="flex h-screen w-screen text-foreground">
          {/* Top to bottom, and FIRST — the rail is a child of the row,
              so it stretches to the full ``h-screen`` height rather
              than to whatever is left between two bars. */}
          <Sidebar />
          {/* The workspace column. ``overflow-hidden`` also resolves
              this column's automatic minimum WIDTH to 0, which is what
              lets a wide table or a DAG canvas inside <main> shrink
              instead of pushing the rail off-screen. */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            {/* ``<main>`` is a plain block + overflow-y-auto. An earlier
                draft made IT a flex column so a nested Footer would push
                to the bottom — Rupert PR-334 r2 #1 flagged that as a
                regression risk (flex items have ``min-height: auto``
                which can prevent the scroll container from clamping,
                breaking ``h-full`` tables / split panes / 3D viewer
                canvases). That still holds, and it now has live
                dependants: the run workspace's split panel (#956) and
                the molecule viewer (#946) both need this clamp.

                #953 moved <main> from a row flex item to a COLUMN one,
                which is the axis where ``min-height: auto`` actually
                bites, so the clamp is now spelled out with ``min-h-0``
                rather than left to the (correct but implicit) rule that
                a non-``visible`` overflow already zeroes it. <main>
                itself stays ``display: block`` — the constraint above
                is about <main>'s OWN display, and the Footer reaches the
                bottom as this column's last child, not by making <main>
                a flex parent. */}
            <main className="min-h-0 flex-1 overflow-y-auto" id="main">
              <ErrorBoundary>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={location.pathname}
                    initial={reduceMotion ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={reduceMotion ? { opacity: 0 } : { opacity: 0 }}
                    transition={{ duration: 0.12 }}
                    /* The page gutter, and the app's only one. ``p-gutter``
                       resolves to ``--gutter``. It sits INSIDE <main>,
                       on the transition wrapper: <main>'s own box is what
                       ``app_frame.spec.ts`` measures against the rail, and
                       padding here cannot reach it.

                       The bottom is the gutter PLUS ``--assistant-clearance``
                       (globals.css carries the arithmetic): the assistant
                       widget is fixed over the page's bottom-right corner, so
                       a page whose last row is a right-aligned action —
                       "Submit Flow" — puts that action under it and it stops
                       being clickable. Both halves are tokens, so the bottom
                       inset still moves with density. */
                    className="p-gutter pb-[calc(var(--gutter)+var(--assistant-clearance))]"
                  >
                    <Outlet />
                  </motion.div>
                </AnimatePresence>
              </ErrorBoundary>
            </main>
            {/* Footer sits OUTSIDE <main> so the browser exposes it as
                the page-level ``contentinfo`` landmark (a <footer>
                nested in <main> is scoped to that main and never
                reaches ``contentinfo``). Rupert PR-334 r2 #2.

                It moved INSIDE the workspace column in #953 so it stops
                spanning under the rail — and that keeps the landmark,
                because ``<footer>`` only loses ``contentinfo`` when it
                descends from a sectioning element (article / aside /
                main / nav / section). Everything between it and <body>
                here is a plain <div>, and it is still a SIBLING of
                <main>. `AppShell.test.tsx` pins both halves. */}
            <Footer />
          </div>
          {/* Overlays, not layout: the palette portals when open and
              renders nothing when closed, the toaster's list and the
              Ruby widget are ``position: fixed``. None of them is an
              in-flow child of the row above, so none takes width from
              the workspace. */}
          <RunCommandPalette />
          <Toaster />
          <ErrorBoundary>
            <RubyWidget currentPath={location.pathname} />
          </ErrorBoundary>
        </div>
      </TooltipProvider>
    </RubyPlatformProvider>
  );
}
